import React, { ElementType, FC, memo, useCallback, useMemo, useRef } from 'react';
import { captureException } from '@sentry/react';
import classnames from 'classnames';
import {
  both,
  chain,
  filter,
  find,
  groupBy,
  head,
  innerJoin,
  isNil,
  keys,
  map,
  not,
  pipe,
  prop,
  propEq,
  values,
} from 'ramda';
import { useHistory } from 'react-router';
import { FullHeightLoader } from '../../../components/common';
import { Lasso } from '../../../components/common/Lasso';
import { SmartGridLayout } from '../../../components/layouts/SmartGrid';
import { FilterName } from '../../../constants/FilterName';
import { LassoItemMargin } from '../../../constants/Lasso';
import { Layout } from '../../../constants/Layout';
import { AllCategoriesId } from '../../../constants/MediaFromWixCategory';
import { MediaType } from '../../../constants/MediaType';
import { MainSource } from '../../../constants/Source';
import { FedOpsInteraction } from '../../../constants/FedOpsInteraction';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { fedopsLogger } from '../../../services/loggers/fedopsLogger';
import styleVariables from '../../../styles/variables.scss';
import { RouteBuilder } from '../../../utils/RouteHelpers';
import { mapIndexed } from '../../../utils/array';
import { ItemClickOptions } from '../../../utils/hoc/withItemsActions/withItemsActions.types';
import { encodePublicMediaFolderId } from '../../../utils/hoc/withPublicMedia/utils';
import { useRelatedItems } from '../../../utils/hoc/withRelatedItems/withRelatedItems';
import { useRootFolder } from '../../../utils/hoc/withRootFolder';
import { useRelatedItemsAnalytics } from '../../../utils/hooks/bi/useRelatedItemsAnalytics';
import { FiltersFindMoreClickOrigins, useTrackSearch } from '../../../utils/hooks/bi/useTrachSearch';
import { useTrackItemSelectAnalytics } from '../../../utils/hooks/bi/useTrackItemSelectAnalytics';
import { ItemSelectOrigin } from '../../../utils/hooks/bi/useTrackItemSelectAnalytics/types';
import { useGetItemPrice } from '../../../utils/hooks/useGetItemPrice';
import { useLoadingErrorCatcher } from '../../../utils/hooks/useLoadingErrorCatcher';
import { useSources } from '../../../utils/hooks/useSources';
import {
  MediaManagerSDKAnyItem,
  MediaManagerSDKAnyItemWithSectionInfo,
  MediaManagerSDKFile,
} from '../../../utils/media-manager-sdk';
import {
  SMART_GRID_ROW_HEIGHT,
  SMART_GRID_SMALL_ROW_HEIGHT,
  SMART_GRID_SMALL_ROW_WINDOW_HEIGHT_THRESHOLD,
  isItemFavorite,
  isSpecificFileType,
  isTransparentVideoFile,
} from '../../../utils/media-manager-sdk/items';
import { isMediaFromWix } from '../../../utils/media-manager-sdk/sources';
import { getActiveItemFilter } from '../../../utils/publicMedia';
import { EmptyState } from '../EmptyState';
import { ExtendedItemsFilters } from '../ExtendedItemsFilters';
import { RelatedItems } from '../RelatedItems/RelatedItems';
import { useWrapperSize } from './hooks/useWrapperSize';
import { calculateFileStyle, createCalculateThumbMinWidth, generateSplitConfig, splitItemsOnRows } from './utils';
import { IOuter, TGridProps } from './SmartGrid.types';
import filesManagerStyles from '../FilesManager.scss';
import styles from './SmartGrid.scss';

const ITEMS_MARGIN = parseInt(styleVariables.smartGridLayoutItemsGap, 10);

type RenderFileType = { tag?: ElementType; isRelatedItem?: boolean; className?: string };

const SmartGridComponent: FC<IOuter> = ({
  items,
  selectedItems,
  selectedItemTypes,
  editingItem,
  source,
  searchQuery,
  currentFolder,
  settingsLayout,
  publicMediaCurrentCategory,
  resetFilesStream,
  hasNoItems,
  hasMoreItems,
  isSecure,
  title,
  isLoading,
  areNextFilesLoading,
  itemsFilters,
  mediaTypes,
  onFileDoubleClick,
  clearItemsSelection,
  loadMoreItems,
  isItemEditing,
  isItemCut,
  isItemSelected,
  onItemClick,
  onItemRightClick,
  onEditItemStart,
  onEditItemCancel,
  onEditItemComplete,
  onItemNameChange,
  openExpandedView,
  selectAllMediaTypes,
  buyItem,
  clearAllExtendedFilters,
  refreshFolder,
  beginDrag,
  selectItem,
  selectItems,
  publicMediaRootCategories,
  decodedFolderId,
  clearRelatedItemsSelection,
}) => {
  const {
    fetchRelatedItems,
    resetRelatedItems,
    relatedItems,
    isRelatedItemsLoading,
    hasNextRelatedItems,
    isRelatedItemsError,
    activeRelatedItemsState,
    setActiveRelatedItemsState,
    resetActiveRelatedItemsState,
  } = useRelatedItems();
  const currentRelatedItemIdRef = useRef(null);

  const { trackOpenRelatedItems } = useRelatedItemsAnalytics();
  const history = useHistory();
  const { isNotAuthorized, isUnknownLoadingError } = useLoadingErrorCatcher();
  const { isExplorableMedia, isFavorites, isTrash, isEnterprise } = useSources({ source, searchQuery });
  const rootFolder = useRootFolder(source);
  const { getItemPrice } = useGetItemPrice();

  const { trackItemsSelected } = useTrackItemSelectAnalytics();

  const [wrapperSize, saveItemsWrapperRef] = useWrapperSize();

  const calculateThumbMinWidth = useCallback(createCalculateThumbMinWidth(getItemPrice), [getItemPrice]);

  const itemFilter = useMemo(() => getActiveItemFilter(itemsFilters, FilterName.Type), [itemsFilters]);
  const subCategoryFilter = useMemo(() => getActiveItemFilter(itemsFilters, FilterName.Category), [itemsFilters]);

  const wixMediaRoot = itemFilter && itemFilter.parentId;
  const isSocialIcons = wixMediaRoot === WixMediaRoot.SocialIcons;
  const isPatterns = itemFilter && itemFilter.id === AllCategoriesId.Patterns;
  const currentSubCategoryId = subCategoryFilter && subCategoryFilter.id;

  const hasNoItemsInCurrentCategory =
    !isLoading &&
    searchQuery &&
    isMediaFromWix(source) &&
    pipe(find(propEq('categoryId', currentSubCategoryId)), isNil)(items);

  const subCategories = pipe(
    values,
    chain(prop('subFolders')),
    groupBy(prop('id')),
    map(head),
  )(publicMediaRootCategories);

  const getCategoryId = (subCategoryId) => subCategories[subCategoryId].parentId;
  const getCategoryName = (subCategoryId) => publicMediaRootCategories[getCategoryId(subCategoryId)].name;

  const itemsFromCurrentCategory =
    searchQuery && !isNil(currentSubCategoryId) && isMediaFromWix(source)
      ? filter(propEq('categoryId', currentSubCategoryId))(items)
      : items;

  const otherCategoriesWithResults = !isNil(currentSubCategoryId)
    ? pipe<
        MediaManagerSDKAnyItemWithSectionInfo[],
        MediaManagerSDKAnyItemWithSectionInfo[],
        { [key: string]: MediaManagerSDKAnyItemWithSectionInfo[] },
        string[],
        { id: string; name: string }[]
      >(
        filter(both(pipe(prop('categoryId'), isNil, not), pipe(propEq('categoryId', currentSubCategoryId), not))),
        groupBy(prop('categoryId')),
        keys,
        map((id) => ({
          id,
          name: getCategoryName(id),
        })),
      )(items as MediaManagerSDKAnyItemWithSectionInfo[])
    : [];

  const { shouldRenderSquareGrid = false, itemRows = [] } = useMemo<TGridProps>(() => {
    if (!wrapperSize.width) {
      return { itemRows: [] };
    }

    const firstItem = head(itemsFromCurrentCategory as MediaManagerSDKFile[]);

    const shouldRenderSquareGrid = Boolean(
      isTransparentVideoFile(firstItem) ||
        isSpecificFileType([MediaType.VectorArt])(firstItem) ||
        isSocialIcons ||
        isPatterns,
    );

    const rowHeight = shouldRenderSquareGrid
      ? isTransparentVideoFile(firstItem)
        ? SMART_GRID_ROW_HEIGHT
        : SMART_GRID_SMALL_ROW_HEIGHT
      : wrapperSize.height < SMART_GRID_SMALL_ROW_WINDOW_HEIGHT_THRESHOLD
      ? SMART_GRID_SMALL_ROW_HEIGHT
      : SMART_GRID_ROW_HEIGHT;

    return {
      shouldRenderSquareGrid,
      itemRows: splitItemsOnRows(
        itemsFromCurrentCategory as MediaManagerSDKFile[],
        generateSplitConfig(wrapperSize.width, rowHeight, ITEMS_MARGIN, shouldRenderSquareGrid),
        calculateThumbMinWidth,
      ),
    };
  }, [itemsFromCurrentCategory, isSocialIcons, isPatterns, wrapperSize, calculateThumbMinWidth]);

  const trackLassoSelectedFinish = useCallback(
    (itemIds: string[]) => {
      const itemsToTrack = innerJoin((item, id) => item.id === id, items, itemIds);
      trackItemsSelected(itemsToTrack, { clickOrigin: ItemSelectOrigin.Lasso }, selectedItemTypes);
    },
    [items, trackItemsSelected, selectedItemTypes],
  );

  const { trackFiltersFindMoreClick } = useTrackSearch();

  const handleCategoryTagClick = useCallback(
    (subCategoryId, event) => {
      event.stopPropagation();

      const categoryId = getCategoryId(subCategoryId);

      history.push(
        RouteBuilder.Search(
          MainSource.MediaFromWix,
          searchQuery,
          encodePublicMediaFolderId({
            categoryId,
            subCategoryId,
          }),
        ),
      );

      trackFiltersFindMoreClick({
        clickOrigin: FiltersFindMoreClickOrigins.FindMore,
        filterCategory: categoryId,
      });
    },
    [getCategoryId, history, searchQuery],
  );

  const shouldShowEmptyState =
    (hasNoItems && currentFolder) || (hasNoItemsInCurrentCategory && publicMediaCurrentCategory);

  const onGetRelatedItems = async (payload = activeRelatedItemsState) => {
    if (!payload) {
      captureException(new Error('Related items onGetRelatedItems - no related items state provided'));
      return;
    }

    const { sourceFile } = payload;
    let success: boolean;

    try {
      fedopsLogger.interactionStarted(FedOpsInteraction.ShowRelatedItems);

      await fetchRelatedItems({
        fileName: sourceFile.fileName,
        folderId: decodedFolderId?.subCategoryId,
        mediaType: sourceFile.mediaType,
      });

      fedopsLogger.interactionEnded(FedOpsInteraction.ShowRelatedItems);

      success = true;
    } catch (e) {
      captureException(e);
      success = false;
    } finally {
      trackOpenRelatedItems(sourceFile, {
        success,
        tabName: source,
        category: subCategoryFilter,
        searchTerm: searchQuery,
      });
    }
  };

  const onRefreshRelatedItems = async () => {
    try {
      if (!activeRelatedItemsState) {
        captureException(new Error('Related Items onRefreshRelatedItems - no active related items state'));
        return;
      }

      const { sourceFile } = activeRelatedItemsState;
      await fetchRelatedItems({
        fileName: sourceFile.fileName,
        folderId: decodedFolderId?.subCategoryId,
        mediaType: sourceFile.mediaType,
        loadNextItems: !!relatedItems.length,
      });
    } catch (e) {
      captureException(e);
    }
  };

  const onRelatedItemsButtonClick = async (
    item: MediaManagerSDKFile,
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    currentRelatedItemIdRef.current = item.id;

    resetRelatedItems();
    setActiveRelatedItemsState({ sourceFile: item });
    onItemClick(item, event, { isRelatedItem: false });
    await onGetRelatedItems({ sourceFile: item });
  };

  const loadNextRelatedItems = useCallback(async () => {
    try {
      if (!activeRelatedItemsState) {
        captureException(new Error('Related Items loadNextRelatedItems - no active related items state'));
        return;
      }

      const { sourceFile } = activeRelatedItemsState;

      await fetchRelatedItems({
        fileName: sourceFile.fileName,
        folderId: decodedFolderId?.subCategoryId,
        loadNextItems: true,
        mediaType: sourceFile.mediaType,
      });
    } catch (e) {
      captureException(e);
    }
  }, [activeRelatedItemsState, fetchRelatedItems, decodedFolderId?.subCategoryId]);

  const onRelatedItemsClose = () => {
    currentRelatedItemIdRef.current = null;

    resetActiveRelatedItemsState();
    clearRelatedItemsSelection();
  };

  const handleItemClick = (
    item: MediaManagerSDKAnyItem,
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    { isRelatedItem }: ItemClickOptions,
  ) => {
    onItemClick(item, event, { isRelatedItem });

    if (!activeRelatedItemsState) {
      return;
    }

    const { ctrlKey, metaKey, shiftKey } = event;

    const { sourceFile } = activeRelatedItemsState;

    if (
      ctrlKey ||
      metaKey ||
      shiftKey ||
      isRelatedItem ||
      !sourceFile.fileName ||
      sourceFile.fileName === (item as MediaManagerSDKFile).fileName
    ) {
      return;
    }

    resetActiveRelatedItemsState();
  };

  const onRelatedItemsOpened = (nodeRef: HTMLDivElement) => {
    nodeRef.scrollIntoView({
      block: 'nearest',
    });
  };

  return (
    <div className={filesManagerStyles.contextMenuWrapper}>
      <div className={filesManagerStyles.content} data-hook="files-manager-content">
        <div className={filesManagerStyles.layoutWrapper}>
          {shouldShowEmptyState ? (
            areNextFilesLoading ? (
              <div className={filesManagerStyles.loader}>
                <FullHeightLoader />
              </div>
            ) : (
              <EmptyState
                items={items}
                refreshFolder={refreshFolder}
                isNotAuthorized={isNotAuthorized}
                isUnknownError={isUnknownLoadingError}
                isEnterprise={isEnterprise}
                currentFolder={currentFolder}
                source={source}
                settingsLayout={settingsLayout}
                searchQuery={searchQuery}
                className={filesManagerStyles.emptyState}
                selectAllMediaTypes={selectAllMediaTypes}
                clearAllExtendedFilters={clearAllExtendedFilters}
                mediaTypes={mediaTypes}
                publicMediaRootCategories={publicMediaRootCategories}
                onItemClick={onItemClick}
                selectedItems={selectedItems}
                isItemSelected={isItemSelected}
                buyItem={buyItem}
                onItemRightClick={onItemRightClick}
                isSecure={isSecure}
                publicMediaCurrentCategory={publicMediaCurrentCategory}
                openExpandedView={openExpandedView}
                extendedFiltersElement={
                  isExplorableMedia &&
                  !isNotAuthorized && (
                    <ExtendedItemsFilters
                      sourceName={rootFolder.name}
                      title={hasNoItems ? null : title}
                      customFilters={hasNoItems ? [] : itemsFilters}
                      isLoading={isLoading}
                      searchQuery={searchQuery}
                      source={source}
                    />
                  )
                }
                dataHook="files-manager-empty-state"
              />
            )
          ) : (
            <Lasso
              itemMarginRight={LassoItemMargin[Layout.SmartGrid]}
              selectedItems={selectedItems}
              onSelectItems={selectItems}
              onSelectFinish={trackLassoSelectedFinish}
            >
              <SmartGridLayout
                isSquare={shouldRenderSquareGrid}
                className={classnames(filesManagerStyles.layout, { [filesManagerStyles.loading]: isLoading })}
                isLoading={isLoading}
                hasMoreItems={hasMoreItems}
                onClick={clearItemsSelection}
                onContextMenu={clearItemsSelection}
                loadMoreItems={loadMoreItems}
                resetFilesStream={resetFilesStream}
                itemsCount={items.length}
                searchQuery={searchQuery}
                otherCategoriesWithResults={otherCategoriesWithResults}
                handleCategoryTagClick={handleCategoryTagClick}
                extendedFiltersElement={
                  isExplorableMedia && (
                    <ExtendedItemsFilters
                      sourceName={currentFolder && currentFolder.name}
                      title={title}
                      customFilters={itemsFilters}
                      isLoading={isLoading}
                      searchQuery={searchQuery}
                      source={source}
                    />
                  )
                }
                selectItem={selectItem}
                saveRef={saveItemsWrapperRef}
              >
                {({ File, LoadingIndicator, itemClassName }) => [
                  // @ts-expect-error #FIXME
                  ...mapIndexed((items: MediaManagerSDKFile[], rowIndex) => {
                    const renderFile = (
                      item: MediaManagerSDKFile,
                      { tag, isRelatedItem, className }: RenderFileType = {},
                    ) => {
                      const { id } = item;
                      const isEditing = isItemEditing(id);
                      const isSelected = isItemSelected(id) && !isEditing;
                      const isCut = isItemCut(id);
                      const thumbMinWidth = calculateThumbMinWidth(item);
                      const fileStyle = calculateFileStyle(item.smartGridSize.width, thumbMinWidth);

                      return (
                        <File
                          key={item.id}
                          renderTag={tag}
                          style={fileStyle}
                          thumbMinWidth={thumbMinWidth}
                          className={classnames(itemClassName, styles.file, className)}
                          isSquare={shouldRenderSquareGrid}
                          backgroundSize={isPatterns ? 'cover' : null}
                          canDrag={!isFavorites && !isTrash}
                          item={(isEditing ? editingItem : item) as MediaManagerSDKFile}
                          selectedItems={isSelected ? selectedItems : null}
                          selected={isSelected}
                          publicMediaCurrentCategory={publicMediaCurrentCategory}
                          isCut={isCut}
                          isEditing={isEditing}
                          isSecure={isSecure}
                          onClick={(item, event) => {
                            handleItemClick(item, event, { isRelatedItem });
                          }}
                          onDoubleClick={onFileDoubleClick}
                          onContextMenu={(item, event) => onItemRightClick(item, event, { isRelatedItem })}
                          onNameChange={onItemNameChange}
                          onRenameStart={onEditItemStart}
                          onRenameComplete={onEditItemComplete}
                          onRenameCancel={onEditItemCancel}
                          onRelatedItemsButtonClick={(event) => {
                            onRelatedItemsButtonClick(item, event);
                          }}
                          openExpandedView={openExpandedView}
                          isFavorite={isItemFavorite(item)}
                          buyItem={buyItem}
                          beginDrag={beginDrag}
                          isRelatedItem={isRelatedItem}
                          currentFolder={currentFolder}
                          shouldShowRelatedItemsButton={isMediaFromWix(source)}
                        />
                      );
                    };

                    const areRelatedItemsShown =
                      activeRelatedItemsState && items.some((item) => item.id === currentRelatedItemIdRef.current);

                    return (
                      <li key={rowIndex} className={styles.itemsRow} onClick={(e) => e.stopPropagation()}>
                        <ul className={styles.itemsRowInner}>{map(renderFile, items)}</ul>
                        <RelatedItems
                          showExitAnimation={!currentRelatedItemIdRef.current}
                          onOpened={onRelatedItemsOpened}
                          isShown={areRelatedItemsShown}
                          isLoading={isRelatedItemsLoading}
                          relatedItems={relatedItems}
                          loadNextItems={loadNextRelatedItems}
                          renderFile={(file) =>
                            renderFile(file, {
                              tag: 'div',
                              isRelatedItem: true,
                              className: styles.relatedItem,
                            })
                          }
                          onClose={onRelatedItemsClose}
                          onRefresh={onRefreshRelatedItems}
                          hasNextRelatedItems={hasNextRelatedItems}
                          isError={isRelatedItemsError}
                        />
                      </li>
                    );
                  }, itemRows),

                  areNextFilesLoading && <LoadingIndicator key="files-loading-indicator" />,
                ]}
              </SmartGridLayout>
            </Lasso>
          )}
        </div>
      </div>
    </div>
  );
};

export const SmartGrid: FC<IOuter> = memo<IOuter>(SmartGridComponent);
